import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getLatestPublicExercises } from '../services/exerciseService'
import {
  submitBetaAccessRequest,
  subscribeBetaAccessRequest,
  deleteOwnBetaAccessRequest,
} from '../services/betaAccess'
import { getExerciseDisplayTitle } from '../utils/exerciseDisplay'
import './StudentBetaRestricted.css'

/**
 * Écran dédié : élève connecté, beta active, sans accès cloud complet (non autorisé).
 * Même pipeline Firestore que la suite Harmonist (betaAccessRequests) ; UX Opus Lab.
 */
export default function StudentBetaRestricted() {
  const { user, logout, enableGuestMode, betaPrivateActive } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [message, setMessage] = useState('')
  const [messageTone, setMessageTone] = useState('neutral')
  const [busy, setBusy] = useState(false)
  const [requestDoc, setRequestDoc] = useState(null)
  const [note, setNote] = useState('')
  const [teasers, setTeasers] = useState([])

  const status = requestDoc?.status ?? null

  const showRequestForm = useMemo(() => {
    if (!status) return true
    if (status === 'rejected') return true
    return false
  }, [status])

  useEffect(() => {
    if (!user?.uid) return undefined
    const unsub = subscribeBetaAccessRequest(user.uid, (data) => {
      setRequestDoc(data)
    })
    return () => unsub()
  }, [user?.uid])

  useEffect(() => {
    if (!betaPrivateActive) return
    getLatestPublicExercises(12, { publicTeaserOnly: true })
      .then(setTeasers)
      .catch(() => setTeasers([]))
  }, [betaPrivateActive])

  const handleRequest = async (e) => {
    e.preventDefault()
    if (!user?.uid || !user.email) return
    setBusy(true)
    setMessage('')
    setMessageTone('neutral')
    try {
      await submitBetaAccessRequest({
        uid: user.uid,
        email: user.email,
        message: note.trim() || undefined,
      })
      setMessageTone('success')
      setMessage('Demande enregistrée. L’équipe vous contactera après validation.')
      setNote('')
    } catch (err) {
      console.error(err)
      setMessageTone('error')
      if (err?.code === 'already_pending') {
        setMessage(err.message)
      } else {
        setMessage(
          err?.code === 'permission-denied'
            ? 'Impossible d’envoyer la demande (permissions).'
            : err?.message || 'Erreur lors de l’envoi.'
        )
      }
    } finally {
      setBusy(false)
    }
  }

  const handleWithdrawApproved = async () => {
    if (!user?.uid) return
    setBusy(true)
    setMessage('')
    setMessageTone('neutral')
    try {
      await deleteOwnBetaAccessRequest(user.uid)
      setMessageTone('success')
      setMessage('Fiche retirée. Vous pouvez soumettre une nouvelle demande ci-dessous.')
    } catch (err) {
      console.error(err)
      setMessageTone('error')
      setMessage(
        err?.code === 'permission-denied'
          ? 'Action impossible (permissions ou accès déjà actif).'
          : err?.message || 'Erreur lors du retrait.'
      )
    } finally {
      setBusy(false)
    }
  }

  const tryDemo = async () => {
    try {
      await logout()
    } catch {
      /* ignore */
    }
    enableGuestMode()
    navigate('/student-dashboard')
  }

  const handleSignOut = async () => {
    try {
      await logout()
    } catch {
      /* ignore */
    }
    navigate('/')
  }

  return (
    <div className="student-beta-restricted">
      <header className="student-beta-restricted-top">
        <button type="button" className="student-beta-restricted-brand" onClick={() => navigate('/')}>
          Opus Lab
        </button>
        <button type="button" className="student-beta-restricted-signout" onClick={handleSignOut}>
          Se déconnecter
        </button>
      </header>

      <main className="student-beta-restricted-main">
        <div className="student-beta-restricted-card">
          <p className="student-beta-restricted-eyebrow">Accès beta privée</p>
          <h1 className="student-beta-restricted-title">Demande d’accès</h1>
          <p className="student-beta-restricted-lead">
            Ce compte n’est pas encore autorisé pour l’ensemble des contenus Opus Lab. Envoyez une
            demande d’accès : elle est traitée depuis le hub central (liste partagée avec les autres
            applications du projet). En attendant, vous pouvez explorer la démo locale ou les
            exercices vitrine.
          </p>

          {user?.email ? (
            <div className="student-beta-restricted-email" title="Compte connecté">
              <span className="student-beta-restricted-email-label">Compte</span>
              <span className="student-beta-restricted-email-value">{user.email}</span>
            </div>
          ) : null}

          {status === 'pending' && (
            <div className="student-beta-restricted-panel student-beta-restricted-panel--pending" role="status">
              <strong>Demande en cours</strong>
              <p>
                Votre demande est en file d’attente. Vous recevrez l’accès dès validation par un
                administrateur (ajout à la liste autorisée). Aucune action requise de votre côté.
              </p>
            </div>
          )}

          {status === 'approved' && (
            <div className="student-beta-restricted-panel student-beta-restricted-panel--approved" role="status">
              <strong>Demande approuvée côté serveur</strong>
              <p>
                Si l’accès complet ne s’affiche pas encore, rechargez la page ou patientez quelques
                secondes. Sinon, vérifiez que votre adresse figure bien dans la liste autorisée
                (hub).
              </p>
              <button
                type="button"
                className="student-beta-restricted-ghost"
                disabled={busy}
                onClick={handleWithdrawApproved}
              >
                Retirer la fiche et soumettre une nouvelle demande
              </button>
            </div>
          )}

          {status === 'rejected' && (
            <div className="student-beta-restricted-panel student-beta-restricted-panel--rejected" role="status">
              <strong>Demande refusée</strong>
              <p>
                Vous pouvez préciser votre contexte ci-dessous et renvoyer une demande : l’envoi
                remplace automatiquement la fiche précédente.
              </p>
            </div>
          )}

          {showRequestForm && (
            <form className="student-beta-restricted-form" onSubmit={handleRequest}>
              <label htmlFor="beta-req-note">Message pour l’équipe (optionnel)</label>
              <textarea
                id="beta-req-note"
                rows={4}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Institution, niveau, lien avec un cours, enseignant référent…"
              />
              <div className="student-beta-restricted-form-footer">
                <span className="student-beta-restricted-charcount">{note.length} / 500</span>
                <button type="submit" className="student-beta-restricted-primary" disabled={busy}>
                  {busy ? 'Envoi…' : status === 'rejected' ? 'Renvoyer une demande' : 'Envoyer la demande'}
                </button>
              </div>
            </form>
          )}

          {message ? (
            <p
              className={`student-beta-restricted-msg${
                messageTone === 'error' ? ' student-beta-restricted-msg--error' : ''
              }${messageTone === 'success' ? ' student-beta-restricted-msg--success' : ''}`}
              role={messageTone === 'error' ? 'alert' : 'status'}
            >
              {message}
            </p>
          ) : null}

          <div className="student-beta-restricted-actions">
            <button type="button" className="student-beta-restricted-secondary" onClick={tryDemo}>
              Essayer la démo (mode invité)
            </button>
            <button type="button" className="student-beta-restricted-secondary" onClick={() => navigate('/')}>
              Retour à l’accueil
            </button>
          </div>

          {teasers.length > 0 && (
            <section className="student-beta-restricted-teasers" aria-labelledby="beta-teasers-h">
              <h2 id="beta-teasers-h">Exercices vitrine</h2>
              <ul>
                {teasers.map((ex) => (
                  <li key={ex.id}>
                    <button
                      type="button"
                      className="student-beta-restricted-teaser-link"
                      onClick={() => navigate(`/play/${ex.id}`, { state: { returnTo: `${location.pathname}${location.search || ''}` } })}
                    >
                      <span className="student-beta-restricted-teaser-title">
                        {getExerciseDisplayTitle(ex.metadata, ex.video?.title)}
                      </span>
                      <span className="student-beta-restricted-demo-pill">Démo</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>
    </div>
  )
}
